import type { Translate } from "@/lib/i18n/translate";
/**
 * The Portal module's contacts table (header + four rows) plus its status-pill.
 * Split out of `module-portal.tsx` to keep each file under the line budget.
 * Styling is 1:1 with the prototype (Hausverwaltung Hero.dc.html, 1118–1148).
 */
import { FileText } from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";

/** Shared grid template for the table header + every row. */
export const PORTAL_TABLE_COLS = "1.7fr 0.9fr 1.9fr 1fr";

type StatusVariant = "ausstehend" | "nicht-eingeladen";

type ContactRow = {
  readonly initials: string;
  readonly avatarBg: string;
  readonly avatarFg: string;
  readonly name: string;
  readonly email: string;
  readonly role: string;
  readonly contract: string;
  readonly location: string;
  readonly status: StatusVariant;
};

function rows(t: Translate): readonly ContactRow[] {
  return [
    {
      initials: "SW",
      avatarBg: "#EAF0F8",
      avatarFg: "#4F77AE",
      name: t("sabineWieland"),
      email: "sabine.wieland@gmx.de",
      role: t("eigentuemerin"),
      contract: t("eigentuemervertrag"),
      location: t("bachstrasse1249809Lingen"),
      status: "ausstehend",
    },
    {
      initials: "TG",
      avatarBg: "#E7F2EC",
      avatarFg: "#3E8E63",
      name: t("thomasGreve"),
      email: "t.greve@web.de",
      role: t("mieter"),
      contract: "Mietvertrag",
      location: t("lindenallee849074Osnabrueck"),
      status: "nicht-eingeladen",
    },
    {
      initials: "AK",
      avatarBg: "#F6E9EE",
      avatarFg: "#B05B79",
      name: t("andreaKoehler"),
      email: "andrea.koehler@outlook.de",
      role: t("eigentuemerin"),
      contract: t("k7Vertraege"),
      location: t("marienstrasse349565Bramsche"),
      status: "nicht-eingeladen",
    },
    {
      initials: "MB",
      avatarBg: "#F0EBF7",
      avatarFg: "#7A5BA8",
      name: t("michaelBorchert"),
      email: "mi.borchert@web.de",
      role: t("eigentuemer"),
      contract: t("eigentuemervertrag"),
      location: "Heinrich-Schniers-Str. 27, 49809 Lingen",
      status: "ausstehend",
    },
  ];
}

/** Status pill (right column of each table row). */
function StatusPill(props: { readonly variant: StatusVariant }): ReactNode {
  const t = useTranslations("marketing.modulePortalTable");
  if (props.variant === "ausstehend") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11.5,
          fontWeight: 600,
          color: "#B07D1F",
          background: "rgba(224,162,59,0.12)",
          padding: "4px 10px",
          borderRadius: 7,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#E0A23B",
          }}
        />
        {t("ausstehend")}
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11.5,
        fontWeight: 600,
        color: "rgba(11,15,25,0.5)",
        background: "#F1F2F4",
        padding: "4px 10px",
        borderRadius: 7,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "rgba(11,15,25,0.3)",
        }}
      />
      {t("nichtEingeladen")}
    </span>
  );
}

/** One contacts-table row. */
function TableRow(props: { readonly row: ContactRow }): ReactNode {
  const r = props.row;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: PORTAL_TABLE_COLS,
        alignItems: "center",
        padding: "11px 4px",
        borderTop: "1px solid #F1F2F4",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}
      >
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: r.avatarBg,
            color: r.avatarFg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11.5,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {r.initials}
        </span>
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: H.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {r.name}
          </span>
          <span
            style={{
              fontSize: 12,
              color: "rgba(11,15,25,0.45)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {r.email}
          </span>
        </div>
      </div>
      <span style={{ fontSize: 13, color: "rgba(11,15,25,0.7)" }}>
        {r.role}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 13,
            color: H.ink,
          }}
        >
          <span style={{ color: "rgba(11,15,25,0.4)", display: "inline-flex" }}>
            <FileText width={14} height={14} strokeWidth={1.8} />
          </span>
          <span style={{ fontWeight: 500 }}>{r.contract}</span>
          <span style={{ color: "rgba(11,15,25,0.3)" }}>›</span>
        </div>
        <div
          style={{
            fontSize: 12,
            color: "rgba(11,15,25,0.45)",
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {r.location}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <StatusPill variant={r.status} />
      </div>
    </div>
  );
}

/** Mono table header + the four contact rows. */
export function PortalTable(): ReactNode {
  const t = useTranslations("marketing.modulePortalTable");
  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: PORTAL_TABLE_COLS,
          alignItems: "center",
          padding: "9px 4px",
          fontFamily: H.label,
          fontSize: 9.5,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "rgba(11,15,25,0.38)",
        }}
      >
        <span>{t("kontakt")}</span>
        <span>{t("rolle")}</span>
        <span>{t("vertraegeAmpLiegenschaften")}</span>
        <span style={{ textAlign: "right" }}>{t("status")}</span>
      </div>
      {rows(t).map((row) => (
        <TableRow key={row.initials} row={row} />
      ))}
    </>
  );
}
